import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';
import type { BookingStatus } from '@/components/booking-grid/types';
import { BOOKING_STATUS_COLORS } from '@/components/booking-grid/types';

export type DisplayMode = 'normal' | 'compact' | 'ultracompact';
export type BookingCornerStyle = 'rounded' | 'sharp';

export interface GridSettings {
  startDate: string; // 'today' | 'YYYY-MM-DD'
  defaultViewPeriod: number;
  weekStartsOn: 0 | 1 | 6;
  statusColors: Record<BookingStatus, string>;
  hideUnpublishedRooms: boolean;
  roomSortOrder: 'room_number' | 'category' | 'floor' | 'sort_order';
  showPricesInCells: boolean;
  displayMode: DisplayMode;
  bookingCornerStyle: BookingCornerStyle;
}

export const GRID_BAR_COLORS: Record<BookingStatus, string> = {
  confirmed: 'bg-blue-500',
  tentative: 'bg-yellow-500',
  checked_in: 'bg-green-500',
  cancelled: 'bg-red-500',
  checked_out: 'bg-gray-400',
  no_show: 'bg-red-700',
  cleaning: 'bg-yellow-400',
  maintenance: 'bg-gray-500',
  overbooking: 'bg-black',
  available: 'bg-white',
  blocked: 'bg-gray-300',
};

const DEFAULT_GRID_SETTINGS: GridSettings = {
  startDate: 'today',
  defaultViewPeriod: 37,
  weekStartsOn: 1,
  statusColors: { ...GRID_BAR_COLORS },
  hideUnpublishedRooms: false,
  roomSortOrder: 'room_number',
  showPricesInCells: true,
  displayMode: 'normal',
  bookingCornerStyle: 'rounded',
};

export function useGridSettings() {
  const { currentHotel } = useOptimizedHotel();

  const { data: rawSettings, isLoading } = useQuery({
    queryKey: ['grid-settings', currentHotel?.id],
    queryFn: async () => {
      if (!currentHotel?.id) return null;

      const { data, error } = await supabase
        .from('hotel_settings')
        .select('key, value')
        .eq('hotel_id', currentHotel.id)
        .eq('category', 'grid');

      if (error) throw error;

      const map: Record<string, any> = {};
      data?.forEach((row) => {
        try {
          map[row.key] = typeof row.value === 'string' ? JSON.parse(row.value) : row.value;
        } catch {
          map[row.key] = row.value;
        }
      });
      return map;
    },
    enabled: !!currentHotel?.id,
    staleTime: 5 * 60 * 1000, // 5 min cache
  });

  const settings: GridSettings = useMemo(() => {
    if (!rawSettings) return DEFAULT_GRID_SETTINGS;

    return {
      startDate: rawSettings.start_date ?? DEFAULT_GRID_SETTINGS.startDate,
      defaultViewPeriod: Number(rawSettings.default_view_period) || DEFAULT_GRID_SETTINGS.defaultViewPeriod,
      weekStartsOn: (rawSettings.week_starts_on != null ? Number(rawSettings.week_starts_on) : DEFAULT_GRID_SETTINGS.weekStartsOn) as 0 | 1 | 6,
      statusColors: rawSettings.status_colors
        ? { ...DEFAULT_GRID_SETTINGS.statusColors, ...rawSettings.status_colors }
        : DEFAULT_GRID_SETTINGS.statusColors,
      hideUnpublishedRooms: rawSettings.hide_unpublished_rooms === true,
      roomSortOrder: rawSettings.room_sort_order ?? DEFAULT_GRID_SETTINGS.roomSortOrder,
      showPricesInCells: rawSettings.show_prices_in_cells !== false,
      displayMode: (rawSettings.display_mode as DisplayMode) ?? (rawSettings.compact_mode === true ? 'compact' : DEFAULT_GRID_SETTINGS.displayMode),
      bookingCornerStyle: rawSettings.booking_corner_style === 'sharp' ? 'sharp' : DEFAULT_GRID_SETTINGS.bookingCornerStyle,
    };
  }, [rawSettings]);

  return { settings, isLoading };
}

export { DEFAULT_GRID_SETTINGS };
